import { publishQueries, usePublishedBoxStore } from '@/entities/sets/publish';
import { useModalStore } from '@/shared/lib/store/model/modal.store';
import { ErrorPopup, InputChk, Modal, Pagination, ToolTip } from '@/shared/ui';
import { gradingSetMap, stateSetMap, useSetMap } from '@/shared/utils/publish';
import { showToast } from '@/shared/utils/toast';
import { useRouter } from '@tanstack/react-router';
import { goReportButtonStyles } from './styles';
import { GoToPhase2Report } from '@/features/navigate';
import { useState } from 'react';

export const ByPublishListTable = ({
    data,
    refetch,
}: {
    data?: API.IntegPubList.Res;
    refetch: () => void;
}) => {
    const { showModal, closeModal } = useModalStore();
    const router = useRouter();
    const { searchPage, setSearchPage } = usePublishedBoxStore();
    const [checkPubIdList, setCheckPubIdList] = useState<DATA.PublishListType[]>([]);

    const { mutate: deleteMutate } = publishQueries.publish.postPublishDelete();

    const handleCheck = (item: DATA.PublishListType) => {
        setCheckPubIdList((prev) => {
            // 이미 체크된 항목인지 확인
            const isChecked = prev.some((checked) => checked.publishId === item.publishId);
            if (isChecked) {
                // 체크 해제: 해당 publishId 제거
                return prev.filter((checked) => checked.publishId !== item.publishId);
            } else {
                // 체크
                return [...prev, item];
            }
        });
    };

    const onDeleteSubmit = () => {
        deleteMutate(
            { publishIds: checkPubIdList.map((item) => item.publishId.toString()) ?? [] },
            {
                onSuccess: (res) => {
                    if (res?.resultOk) {
                        showToast('발행 목록 삭제 완료');
                        setCheckPubIdList([]);
                        refetch();
                    } else showToast('발행 목록 삭제 실패', 2000, 'error');
                },
                onError: () =>
                    showModal({
                        isCustom: true,
                        id: 'error',
                        initContents: () => <ErrorPopup />,
                    }),
            }
        );
    };

    const onCheckAll = (state: boolean) => {
        if (!data?.publishList) return;
        if (state) {
            const combined = [...checkPubIdList, ...data.publishList];
            const unique = [...new Map(combined.map((obj) => [obj.publishId, obj])).values()];
            setCheckPubIdList(unique);
        } else {
            const filtered = checkPubIdList.filter(
                (checked) => !data.publishList.some((item) => item.publishId === checked.publishId)
            );
            setCheckPubIdList(filtered);
        }
    };

    const isAllIncluded = data?.publishList?.every((item) =>
        checkPubIdList.some((checked) => checked.publishId === item.publishId)
    );

    const hasProgress = checkPubIdList.some((item) => item.sttsNm === '진행중');

    const onDelete = () => {
        if (checkPubIdList.length === 0) return;
        hasProgress ? openAlertModal() : openDeleteModal();
    };

    const openDeleteModal = () => {
        showModal({
            isCustom: true,
            id: 'publish-delete',
            initContents: () => (
                <Modal
                    open={true}
                    overlay={false}
                    onClose={() => closeModal('publish-delete')}
                    title="발행 목록 삭제"
                    description={
                        <>
                            선택한 발행 목록을 모두 삭제하시겠습니까?
                            <br />
                            삭제한 발행 목록은 복구할 수 없습니다.
                        </>
                    }
                    onConfirm={onDeleteSubmit}
                    onConfirmText="삭제"
                ></Modal>
            ),
        });
    };

    const openAlertModal = () => {
        showModal({
            isCustom: true,
            id: 'no-publish-delete',
            initContents: () => (
                <Modal
                    open={true}
                    overlay={false}
                    onClose={() => closeModal('no-publish-delete')}
                    title="안내"
                    description="진행 중인 평가는 삭제할 수 없습니다."
                    onConfirm={() => closeModal('no-publish-delete')}
                    onConfirmText="확인"
                ></Modal>
            ),
        });
    };

    return (
        <div className="table-board // con-mt3">
            <div className="table-board__body table-style1">
                <table>
                    <colgroup>
                        <col style={{ width: '66px' }} />
                        <col style={{ width: '72px' }} />
                        <col style={{ width: '90px' }} />
                        <col />
                        <col style={{ width: '140px' }} />
                        <col style={{ width: '104px' }} />
                        <col style={{ width: '104px' }} />
                        <col style={{ width: '140px' }} />
                    </colgroup>
                    <thead>
                        <tr>
                            <th scope="col">
                                <InputChk
                                    type="checkbox"
                                    forId="boardChkAll"
                                    checked={isAllIncluded}
                                    onChange={(e) => onCheckAll(e.target.checked)}
                                >
                                    <span className="blind">전체선택</span>
                                </InputChk>
                            </th>
                            <th scope="col">NO</th>
                            <th scope="col">용도</th>
                            <th scope="col">발행한 자료</th>
                            <th scope="col">응시 기간</th>
                            <th scope="col">제출 인원</th>
                            <th scope="col">채점 상태</th>
                            <th scope="col">리포트</th>
                        </tr>
                    </thead>
                    <tbody className="align-center">
                        {data?.publishList.map((item, idx) => {
                            return (
                                <tr key={`publist-table-${idx}`}>
                                    <td>
                                        <InputChk
                                            type="checkbox"
                                            forId={`chkrow${idx + 1}`}
                                            checked={checkPubIdList.includes(item)}
                                            onChange={() => handleCheck(item)}
                                        >
                                            <span className="blind">{item.publishId} 선택</span>
                                        </InputChk>
                                    </td>
                                    <td className="txt1">{data.page.number * 10 + idx + 1}</td>
                                    <td>
                                        <span className={`row-use ${useSetMap[item.publishUse]}`}>
                                            {item.publishUse}
                                        </span>
                                    </td>
                                    <td className="row-link">
                                        <button
                                            type="button"
                                            className="link"
                                            onClick={() => {
                                                router.navigate({
                                                    to: `/sets/published-box/detail/${item.publishId}`,
                                                });
                                            }}
                                        >
                                            <span className="txt">{item.publishNm}</span>
                                            {item.sttsNm && (
                                                <span
                                                    className={`row-state ${stateSetMap[item.sttsNm]}`}
                                                >
                                                    {item.sttsNm}
                                                </span>
                                            )}
                                        </button>
                                    </td>
                                    {/* <td className="txt1">{item.claNm}</td> */}
                                    <td className="txt1 align-left">
                                        {item.pdPublishStDt
                                            ? `${item.pdPublishStDt}~
                    ${item.pdPublishEdDt}`
                                            : '-'}
                                    </td>
                                    <td>
                                        <ToolTip
                                            msg={
                                                <p className="tip-txt">
                                                    상세보기에서 학생을
                                                    <br /> 추가할 수 있습니다.
                                                </p>
                                            }
                                        >
                                            <span className="row-count">
                                                {(item.publishType !== 2 &&
                                                    !!item.targetCnt &&
                                                    item.targetCnt !== 0) ||
                                                (item.publishType === 2 && item.submitCnt !== 0) ? (
                                                    <>
                                                        <span className="num">
                                                            {item.submitCnt}
                                                        </span>{' '}
                                                    </>
                                                ) : (
                                                    '-'
                                                )}
                                            </span>
                                        </ToolTip>
                                    </td>
                                    <td
                                        className={
                                            item.checkStts
                                                ? `row-grading ${gradingSetMap[item.checkStts]}`
                                                : ''
                                        }
                                    >
                                        {item.checkStts ? item.checkStts : '-'}
                                    </td>
                                    <td>
                                        {item.sttsNm === '종료' ? (
                                            <ToolTip
                                                msg={
                                                    <p className="tip-txt">
                                                        응시 종료 후 리포트 생성에 <br />약 3분 정도
                                                        소요됩니다.
                                                    </p>
                                                }
                                            >
                                                <GoToPhase2Report
                                                    ct="weclass"
                                                    gubun="t"
                                                    trgtId={item.trgtId}
                                                    publishType={item.publishType}
                                                    textbkId={item?.textbkId}
                                                    styles={goReportButtonStyles}
                                                    claId={item.claId}
                                                    // stntId={item?.stntId ?}
                                                    // nickname={item?.nickname}
                                                />
                                                {/* <GoToReport
                                                    isStnts={true}
                                                    trgtId={item.trgtId}
                                                    publishType={item.publishType.toString()}
                                                    styles={goReportButtonStyles}
                                                    claId={item?.claId}
                                                    textbkId={item?.textbkId}
                                                /> */}
                                            </ToolTip>
                                        ) : (
                                            '-'
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <div className="table-board__footer">
                <div className="pos-left">
                    <button type="button" className="btn btn-default line small" onClick={onDelete}>
                        선택 삭제
                    </button>
                </div>
                {/* {children} */}
                <Pagination
                    currentPage={searchPage}
                    totalPages={data?.page.totalPages!}
                    onChange={(page: number) => setSearchPage(page)}
                    pageBtnCount={10}
                />
            </div>
        </div>
    );
};
